import { isDefined, RecommendedCompsSources } from '@aloft/toolkit-shared';
import { Block, Flex, Icon, ProductColor, Tooltip, Typography, theme } from '@aloft/toolkit-sui';
import {
  SortingState,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { Dispatch, SetStateAction, useEffect, useMemo, useRef } from 'react';

import {
  LocationType,
  formatProximityRequirementTooltip,
} from '../../../utils/locationTypeProximityRequirementMiles.ts';
import { addressColumn } from '../internal/gridHelpers/addressColumn.tsx';
import { getSortedRowModelWithPinnedSubject } from '../internal/gridHelpers/getSortedRowModelWithPinnedSubject.ts';
import { makeSelectionColumn } from '../internal/gridHelpers/selectionColumn.tsx';
import { statusColumn } from '../internal/gridHelpers/statusColumn.tsx';
import { CompSelectionProperty, CompSelectionState } from '../types.ts';

const INITIAL_PAGE_INDEX = 0;
/*
We probably don't need to worry about paging. We should just be able to load
everything we get back, which should be less than this, and users can scroll
through.
*/
const PAGE_SIZE = 500;

function formatDateCell(date?: { format: (fmt: string) => string }) {
  return date ? date.format('MM/yy') : '';
}

function booleanToYesNo(value?: boolean) {
  if (value === undefined) return '';
  return value ? 'Yes' : 'No';
}

function formatAddressCell(address: CompSelectionProperty['address']) {
  return `${address?.AddressLineText}, ${address?.CityName}, ${address?.StateCode} ${address?.PostalCode}`;
}

export function useRecommendedCompSelectionTable(
  data: CompSelectionProperty[],
  subject: CompSelectionProperty,
  comps: CompSelectionProperty[],
  sorting: SortingState,
  setSorting: Dispatch<SetStateAction<SortingState>>,
  rowSelection: CompSelectionState,
  setRowSelection: Dispatch<SetStateAction<CompSelectionState>>,
  source: RecommendedCompsSources,
  orderID: string,
  isInspectifyOrder: boolean,
  hasArvOption: boolean,
  locationType: LocationType | undefined,
  proximityRequirementMiles: number | undefined,
) {
  const tableData = useMemo(() => [subject, ...data], [subject, data]);

  const rowsToPin = 1;

  // Use a ref to store the current rowSelection so cells always read the latest value
  // without causing the column definition to be recreated
  const rowSelectionRef = useRef<CompSelectionState>(rowSelection);
  useEffect(() => {
    rowSelectionRef.current = rowSelection;
  }, [rowSelection]);

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<CompSelectionProperty>();

    return [
      makeSelectionColumn(
        () => rowSelectionRef.current,
        setRowSelection,
        isInspectifyOrder,
        hasArvOption,
      ),
      columnHelper.accessor((p) => p.mlsStatus, {
        id: 'Status',
        cell: ({ table, row }) => {
          const sortedRows = table.getSortedRowModel().rows;
          const visibleIndex = sortedRows.findIndex((r) => r.id === row.id);
          return statusColumn(row.original, visibleIndex);
        },
      }),
      columnHelper.accessor((p) => formatAddressCell(p.address), {
        id: 'Address',
        cell: ({ row }) => addressColumn(row.original),
      }),
      columnHelper.accessor((p) => p.similarityScore, {
        id: 'Similarity Score',
        cell: ({ row: { original } }) =>
          isDefined(original.similarityScore) ? original.similarityScore.toFixed(2).toString() : '',
      }),
      columnHelper.accessor((p) => p.distanceToSubject, {
        id: 'Proximity',
        cell: ({ row: { original } }) => {
          if (original.id === 'subject' || original.distanceToSubject === undefined) return '';

          const distanceText = `${original.distanceToSubject} miles`;
          const showWarning =
            locationType !== undefined &&
            proximityRequirementMiles !== undefined &&
            original.distanceToSubject > proximityRequirementMiles;

          return (
            <Flex alignItems="center" justifyContent="space-between" width="100%" gap="8px">
              <Typography variant="body2" color="textPrimary">
                {distanceText}
              </Typography>
              <Block width="18px" height="18px">
                {showWarning && (
                  <Tooltip
                    label={formatProximityRequirementTooltip(locationType!, proximityRequirementMiles!)}
                  >
                    <Block
                      width="18px"
                      height="18px"
                      __dangerDoNotUseSx={{
                        backgroundColor: theme.colors.alertWarning,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon icon="warning" color="textOffset" size="sm" />
                    </Block>
                  </Tooltip>
                )}
              </Block>
            </Flex>
          );
        },
      }),
      columnHelper.accessor((p) => p.saleDate, {
        id: 'Sale Date',
        cell: ({ row: { original } }) => formatDateCell(original.saleDate),
      }),
      columnHelper.accessor((p) => p.contractDate, {
        id: 'Contract Date',
        cell: ({ row: { original } }) => formatDateCell(original.contractDate),
      }),
      columnHelper.accessor((p) => p.cumulativeDaysOnMarket, {
        id: 'CDOM',
        cell: ({ row: { original } }) => original.cumulativeDaysOnMarket?.toString(),
      }),
      columnHelper.accessor((p) => p.salePrice?.toNumericPrice(), {
        id: 'Sale Price',
        cell: ({ row: { original } }) => original.salePrice?.toString(true),
      }),
      columnHelper.accessor((p) => p.listPrice?.toNumericPrice(), {
        id: 'List Price',
        cell: ({ row: { original } }) => original.listPrice?.toString(true),
      }),
      hasArvOption
        ? columnHelper.accessor((p) => p.qualityTier, {
          id: 'Quality Tier',
          cell: ({ row: { original } }) => {
            if (original.qualityTier === undefined) return '';
            return (
              <Flex alignItems="center" gap="8px">
                <Block
                  width="16px"
                  height="16px"
                  backgroundColor={`qualityTier${original.qualityTier}` as ProductColor}
                />
                Tier {original.qualityTier}
              </Flex>
            );
          },
        })
        : undefined,
      columnHelper.accessor((p) => p.propertyType, {
        id: 'Property Type',
        cell: ({ row }) => row.original.propertyType,
      }),
      columnHelper.accessor((p) => p.yearBuilt, {
        id: 'Year Built',
        cell: ({ row: { original } }) => original.yearBuilt?.toString(),
      }),
      columnHelper.accessor((p) => p.gla?.toSqFt().getUnitAmount(), {
        id: 'GLA',
        cell: ({ row: { original } }) => original.gla?.toString(),
      }),
      columnHelper.accessor((p) => p.lotSize?.toSqFt().getUnitAmount(), {
        id: 'Lot Size',
        cell: ({ row: { original } }) => original.lotSize?.toRoundedString(),
      }),
      columnHelper.accessor((p) => p.salePrice, {
        id: 'Sqft Price',
        cell: ({ row: { original } }) => {
          const glaSqft = original.gla?.toSqFt().getUnitAmount();
          return original.salePrice && glaSqft
            ? `${original.salePrice.div(glaSqft || 1).toString(true)}/sqft`
            : '';
        },
      }),
      columnHelper.accessor((p) => p.bedrooms, {
        id: 'Beds',
        cell: ({ row: { original } }) => original.bedrooms?.toString(),
      }),
      columnHelper.accessor((p) => p.fullBaths, {
        id: 'Full Baths',
        cell: ({ row: { original } }) => original.fullBaths?.toString(),
      }),
      columnHelper.accessor((p) => p.halfBaths, {
        id: 'Half Baths',
        cell: ({ row: { original } }) => original.halfBaths?.toString(),
      }),
      columnHelper.accessor((p) => p.garage, {
        id: 'Garage',
        cell: ({ row: { original } }) => booleanToYesNo(original.garage),
      }),
      columnHelper.accessor((p) => p.garageCount, {
        id: 'Garage Count',
        cell: ({ row: { original } }) => original.garageCount?.toString(),
      }),
      columnHelper.accessor((p) => p.basement, {
        id: 'Basement',
        cell: ({ row: { original } }) => booleanToYesNo(original.basement),
      }),
      columnHelper.accessor((p) => p.pool, {
        id: 'Pool',
        cell: ({ row: { original } }) => booleanToYesNo(original.pool),
      }),
      columnHelper.accessor((p) => p.reo, {
        id: 'REO',
        cell: ({ row: { original } }) => booleanToYesNo(original.reo),
      }),
      columnHelper.accessor((p) => p.shortSale, {
        id: 'Short Sale',
        cell: ({ row: { original } }) => booleanToYesNo(original.shortSale),
      }),
    ].filter(isDefined);
  }, [
    comps.length,
    hasArvOption,
    isInspectifyOrder,
    locationType,
    orderID,
    proximityRequirementMiles,
    rowsToPin,
    setRowSelection,
    source,
  ]);

  const table = useReactTable({
    data: tableData,
    columns,
    enableSorting: true,
    enableRowSelection: false,
    getRowId: (row) => row.id,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModelWithPinnedSubject(rowsToPin),
    getPaginationRowModel: getPaginationRowModel(),
    state: { sorting },
    initialState: {
      pagination: {
        pageIndex: INITIAL_PAGE_INDEX,
        pageSize: PAGE_SIZE,
      },
    },
  });

  return { table, rowsToPin, tableData };
}
